import { Link } from '@tanstack/react-router'
import { setTheme, useDarkMode } from '~/lib/browser/theme'
import { Container } from '~/components/ui/Container'
import { SocialIcon } from '~/components/icons'
import { SOCIAL_LINKS } from '~/lib/site/constants'

// Build year (pages are prerendered; reading the clock during render is impure).
const YEAR = new Date().getFullYear()

/** `m` is the position in the stacked (mobile) order; array order is the desktop column-flow order. */
const links = [
  { m: 1, href: '/about-us', label: 'About us' },
  { m: 3, href: '/contact-us', label: 'Contact us' },
  { m: 6, href: 'https://adb.dxe.io/apply', label: 'Join us', external: true },
  { m: 4, href: '/whistleblower-support', label: 'Whistleblower support' },
  { m: 2, href: '/news/press-releases', label: 'Press releases' },
  { m: 5, href: '/news/blog', label: 'Blog' },
  { m: 7, href: '/chapters', label: 'Find a chapter' },
  { m: 8, href: '/resources', label: 'Resources for activists' },
]

const mobileOrder: Record<number, string> = {
  1: 'max-lg:order-1',
  2: 'max-lg:order-2',
  3: 'max-lg:order-3',
  4: 'max-lg:order-4',
  5: 'max-lg:order-5',
  6: 'max-lg:order-6',
  7: 'max-lg:order-7',
  8: 'max-lg:order-8',
}

const linkClass =
  'text-lg leading-[22px] font-normal text-white transition-colors hover:text-footer-text'

export function SiteFooter() {
  return (
    <footer className="bg-black py-[50px] md:py-[100px]">
      <Container className="grid gap-y-[50px] lg:grid-cols-3 lg:gap-4">
        {/* Logo, tagline and copyright: first column on desktop, last on mobile. */}
        <div className="max-lg:order-last">
          <Link
            to="/"
            className="transition-opacity hover:opacity-80 max-md:hidden"
          >
            <img
              src="/images/logo-dxe-footer.png"
              width={363}
              height={48}
              alt="Direct Action Everywhere"
              className="mb-[50px] w-[363px] max-w-full"
            />
          </Link>
          <p className="mb-0 text-xl leading-[26px] text-footer-text">
            Until every animal is free
          </p>
          <Link to="/privacy-policy" className="group block">
            <p className="mb-0 text-sm leading-[26px] font-light text-footer-text transition-colors group-hover:text-white">
              &copy; {YEAR} Direct Action Everywhere
              <br />
              Privacy Policy
            </p>
          </Link>
        </div>

        <ul className="grid list-none gap-4 p-0 lg:grid-cols-2 lg:grid-rows-4 lg:grid-flow-col">
          {links.map((l) => (
            <li key={l.href} className={mobileOrder[l.m]}>
              {l.external ? (
                <a
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  className={linkClass}
                >
                  {l.label}
                </a>
              ) : (
                <Link to={l.href} className={linkClass}>
                  {l.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Desktop: the same four rows as the links, social icons level with
            the second and the theme toggle with the last. */}
        <div className="flex flex-col items-start gap-10 max-lg:pt-[35px] lg:grid lg:grid-rows-4 lg:gap-4 lg:self-start lg:pl-[50px]">
          <div className="flex gap-6 lg:row-start-2 lg:gap-4 lg:self-center">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="text-white transition-colors hover:text-footer-text"
              >
                <SocialIcon name={s.name} className="size-8" />
              </a>
            ))}
          </div>
          <div className="lg:row-start-4 lg:self-center">
            <DarkModeToggle />
          </div>
        </div>
      </Container>
    </footer>
  )
}

/**
 * Light/dark switch (src/lib/browser/theme.ts): sun on the left, moon on the right,
 * and the knob sits under the active one. Positions and colours come from
 * `dark:` CSS, so the prerendered HTML is right before hydration.
 */
function DarkModeToggle() {
  const dark = useDarkMode()
  const icon = 'relative size-3.5 transition-colors'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label="Dark mode"
      onClick={() => setTheme(!dark)}
      data-theme-toggle
      className="relative flex h-6 w-12 cursor-pointer items-center justify-between rounded-full bg-white/20 px-[5px] transition-colors hover:bg-white/30"
    >
      <span
        aria-hidden="true"
        className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white transition-transform dark:translate-x-6"
      />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        aria-hidden="true"
        className={`${icon} text-black dark:text-white/60`}
      >
        <circle cx="12" cy="12" r="4.5" />
        <path d="M12 1.5v2.5M12 20v2.5M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M1.5 12H4M20 12h2.5M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8" />
      </svg>
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={`${icon} text-white/60 dark:text-black`}
      >
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
      </svg>
    </button>
  )
}
